import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, XCircle, AlertTriangle, UploadCloud } from 'lucide-react';
import { useApi, ApiError } from '../lib/api';
import { dateTimeCentral, periodLabel } from '../lib/format';

interface Issue {
  sheet?: string;
  cell?: string;
  row?: number;
  message: string;
}

interface UploadResult {
  upload_id: number;
  status: 'accepted' | 'rejected';
  period: string;
  errors: Issue[];
  warnings: Issue[];
  line_count: number | null;
  unmapped_accounts?: { rowcal_account: string; description: string | null }[];
}

interface UploadRow {
  upload_id: number;
  period: string;
  filename: string;
  status: string;
  uploaded_by: string;
  uploaded_at: string;
  line_count: number | null;
  error_count: number;
}

function defaultPeriod(): string {
  // Prior month in Central time — Rowcal sends after month-end.
  const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  now.setDate(1);
  now.setMonth(now.getMonth() - 1);
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function IssueList({ title, issues, tone }: { title: string; issues: Issue[]; tone: 'error' | 'warn' }) {
  if (!issues.length) return null;
  const Icon = tone === 'error' ? XCircle : AlertTriangle;
  return (
    <div className="mt-4">
      <h3 className={`flex items-center gap-2 font-semibold ${tone === 'error' ? 'text-negative' : 'text-allstar-gray-dark'}`}>
        <Icon className="w-4 h-4" /> {title} ({issues.length})
      </h3>
      <ul className="mt-2 text-sm space-y-1">
        {issues.map((i, n) => (
          <li key={n} className="flex gap-2">
            <span className="font-mono text-xs text-allstar-gray-dark w-28 shrink-0">
              {[i.sheet, i.cell ?? (i.row ? `row ${i.row}` : null)].filter(Boolean).join(' ! ')}
            </span>
            <span>{i.message}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Upload() {
  const api = useApi();
  const qc = useQueryClient();
  const [period, setPeriod] = useState(defaultPeriod());
  const [file, setFile] = useState<File | null>(null);

  const history = useQuery({ queryKey: ['uploads'], queryFn: () => api.get<UploadRow[]>('/api/uploads') });

  const upload = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('period', period);
      form.append('file', file!);
      return api.upload<UploadResult>('/api/uploads', form);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['uploads'] }),
  });

  // A rejected file comes back as a 422 whose body is the full result.
  const result: UploadResult | undefined =
    upload.data ?? (upload.error instanceof ApiError && upload.error.status === 422 ? upload.error.body : undefined);

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h1 className="text-2xl text-denim">Rowcal Upload</h1>
        <p className="text-sm text-allstar-gray-dark mt-1">
          Load Rowcal's monthly Excel file. The file is checked against the expected format before anything is saved.
        </p>
      </div>

      <div className="card p-5 space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <label className="text-sm">
            <div className="font-semibold mb-1">Period</div>
            <input type="month" className="border border-allstar-gray rounded px-3 py-2" value={period}
              onChange={(e) => setPeriod(e.target.value)} />
          </label>
          <label className="text-sm flex-1 min-w-64">
            <div className="font-semibold mb-1">Excel file (.xlsx)</div>
            <input type="file" accept=".xlsx,.xlsm" className="block w-full text-sm"
              onChange={(e) => { setFile(e.target.files?.[0] ?? null); upload.reset(); }} />
          </label>
          <button className="btn-primary" disabled={!file || !period || upload.isPending} onClick={() => upload.mutate()}>
            <UploadCloud className="w-4 h-4" /> {upload.isPending ? 'Checking…' : 'Upload'}
          </button>
        </div>

        {upload.error && !result && <div className="text-negative text-sm">{(upload.error as Error).message}</div>}

        {result && (
          <div className={`rounded border p-4 ${result.status === 'accepted' ? 'border-positive' : 'border-negative'}`}>
            {result.status === 'accepted' ? (
              <div className="flex items-center gap-2 text-positive font-semibold">
                <CheckCircle2 className="w-5 h-5" /> Accepted — {result.line_count} lines for {periodLabel(result.period)}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-negative font-semibold">
                <XCircle className="w-5 h-5" /> Rejected — fix the issues below and re-upload. Nothing was saved to the period.
              </div>
            )}
            <IssueList title="Errors" issues={result.errors} tone="error" />
            <IssueList title="Warnings" issues={result.warnings} tone="warn" />
            {!!result.unmapped_accounts?.length && (
              <p className="mt-4 text-sm">
                <AlertTriangle className="inline w-4 h-4 text-caution" /> {result.unmapped_accounts.length} Rowcal
                account(s) have no GL mapping yet — map them on the GL Mapping page before generating outputs.
              </p>
            )}
          </div>
        )}
      </div>

      <div className="card overflow-hidden">
        <table className="table-default">
          <thead>
            <tr>
              <th>Period</th><th>File</th><th>Status</th><th className="num">Lines</th><th>By</th><th>When (CT)</th>
            </tr>
          </thead>
          <tbody>
            {history.data?.map((u) => (
              <tr key={u.upload_id}>
                <td>{periodLabel(u.period)}</td>
                <td className="truncate max-w-56">{u.filename}</td>
                <td>
                  <span className={u.status === 'accepted' ? 'text-positive font-semibold' : u.status === 'rejected' ? 'text-negative' : 'text-allstar-gray-dark'}>
                    {u.status}{u.status === 'rejected' ? ` (${u.error_count})` : ''}
                  </span>
                </td>
                <td className="num">{u.line_count ?? '—'}</td>
                <td>{u.uploaded_by}</td>
                <td>{dateTimeCentral(u.uploaded_at)}</td>
              </tr>
            ))}
            {history.data?.length === 0 && (
              <tr><td colSpan={6} className="text-center text-allstar-gray-dark py-6">No uploads yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
